import { Component, Element, Host, Method, Prop, State, h } from '@stencil/core';

import { onQuestionAnswered, setModificationQuestionVisible } from '../../../actions/actions.answers';
import EventBus from '../../../services/service.eventBus';
import { BrowserUtils } from '../../../utils/utils.browser';
import { QUESTION_TOGGLE_MODIFICATIONS } from '../../../utils/utils.question';
import XIcon from '../svg/XIcon';

export type IcoModalSize = 'default' | 'large';

@Component({
  tag: 'ico-counteroffer-modal',
  styleUrl: 'ico-counteroffer-modal.css',
})
export class IcoCounterofferModal {
  @Element() el: HTMLElement;

  elContentContainer: HTMLElement;

  @Prop() dataTestId: string = '';
  @Prop() shouldCloseOnBackdrop: boolean = true;
  @Prop() displayX: boolean = false;
  @Prop() closeModalKeepModsSelections: boolean = false;
  @Prop() paddingAround: boolean = false;
  @Prop() callback = () => {};
  @Prop() borderedFooter: boolean = false;
  @Prop() modalSize: IcoModalSize = 'default';
  @Prop() hideOverflow: boolean = false;
  @Prop() modalPositionOffsetTop: string = '';
  @Prop() autoOverflow: boolean = true;

  @State() isOpen: boolean = false;
  @State() scrollLocation = 0;
  @State() viewportHeight: number = window.innerHeight;

  @Method()
  async open() {
    this.isOpen = true;
    this.lockBodyScrolling();
    this.updateViewportHeight();

    EventBus.publish('IsModalOpen', true);
  }

  @Method()
  async close(hasKeepMods = false) {
    this.isOpen = false;
    this.unLockBodyScrolling();
    this.resetScrollbar();
    this.callback();

    if (hasKeepMods) {
      onQuestionAnswered(QUESTION_TOGGLE_MODIFICATIONS, true);
      setModificationQuestionVisible();
    }

    EventBus.publish('IsModalOpen', false);
  }

  connectedCallback() {
    window.addEventListener('resize', this.updateViewportHeight);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.updateViewportHeight);
  }

  private updateViewportHeight = () => {
    this.viewportHeight = window.innerHeight;
  };

  onBackDropClick = (ev) => {
    if (!this.shouldCloseOnBackdrop) {
      return;
    }
    const isClickInsideModal = typeof ev.composedPath !== 'function' || ev.composedPath().some((el) => el === this.el.querySelector("[role='modalCard']"));

    if (isClickInsideModal) {
      return;
    }

    this.close(this.closeModalKeepModsSelections);
  };

  // See https://css-tricks.com/prevent-page-scrolling-when-a-modal-is-open/
  lockBodyScrolling() {
    const { scrollY } = window;
    this.scrollLocation = scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100vw';
    document.body.style.height = '100vh';
  }

  unLockBodyScrolling() {
    document.body.style.position = 'relative';
    document.body.style.top = 'auto';
    document.body.style.width = 'auto';
    document.body.style.height = 'auto';
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore typescript is missing 'instant' as valid type for scroll behavior
    window.scrollTo({ top: this.scrollLocation, behavior: 'instant' });
  }

  // Fix scrollbar position issue triggered on subsequent openings
  resetScrollbar() {
    this.elContentContainer.scrollTo(0, 0);
  }

  handleSlotChange() {
    this.resetScrollbar();
  }

  renderOverlay() {
    const backdropClasses = {
      'flex justify-center items-center fixed z-10 overflow-y-auto bg-black/50': true,
      'p-4': this.paddingAround,
      'w-screen h-screen': true, // Always use full viewport dimensions
      'top-0 left-0': true, // Always anchor to top-left of viewport
    };
    const modalClasses = {
      'flex flex-col max-w-[1200px] min-w-312 bg-white rounded-lg align-middle text-left overflow-auto shadow-xl': true,
      'm-auto': this.paddingAround,
      'mx-auto my-0': !this.paddingAround,
      absolute: this.modalPositionOffsetTop !== '',
      relative: this.modalPositionOffsetTop === '',
      'max-h-[45vh]': BrowserUtils.IN_IFRAME(),
      'max-h-[80vh]': !BrowserUtils.IN_IFRAME(),
    };

    const contentClasses = {
      'xs:px-4 md:px-8 pt-5 flex flex-col max-h-[80vh] overflow-hidden': true,
    };
    const footerClass = {
      'border-solid border-t-2 border-x-0 border-b-0 border-ico-neutral-3': this.borderedFooter,
      'py-5 xs:px-4 md:px-8': true,
    };
    const headerClass = { 'text-ico-heading mb-4': true, 'mr-10': this.displayX };

    const xClasses = {
      'ico-modal-x': true,
    };
    const contentMainClass = {
      'md:max-h-96': this.modalSize === 'default',
      'pr-1.5': true,
      'overflow-auto': this.autoOverflow,
      'mdLrg:overflow-hidden': this.hideOverflow,
      'max-w-4xl': this.modalSize === 'default',
      'max-w-5xl': this.modalSize === 'large',
    };
    // Calculate viewport-relative positioning instead of parent-relative
    const modalOffsetStyle =
      this.modalPositionOffsetTop !== ''
        ? {
            top: `${this.modalPositionOffsetTop}px`,
          }
        : {
            top: 'unset',
            bottom: BrowserUtils.IN_IFRAME() ? `${Math.floor(this.viewportHeight / 4)}px` : '0px',
          };

    return (
      <div data-testId={`${this.dataTestId}-modal`} class={backdropClasses} role="dialog" aria-modal="true" onClick={this.onBackDropClick}>
        <div role="modalCard" class={modalClasses} style={modalOffsetStyle}>
          {this.displayX && (
            <button data-testId={`${this.dataTestId}-modal-close-button`} onClick={() => this.close(this.closeModalKeepModsSelections)} class={xClasses}>
              <XIcon />
            </button>
          )}
          <div class={contentClasses}>
            <div data-testId={`${this.dataTestId}-modal-header`} class={headerClass}>
              <slot name="header" />
            </div>
            <div data-testId={`${this.dataTestId}-modal-main`} class={contentMainClass} ref={(el) => (this.elContentContainer = el as HTMLElement)}>
              <slot name="main" onSlotchange={() => this.handleSlotChange()} />
              {/* Always have a catch all slot */}
              <slot onSlotchange={() => this.handleSlotChange()} />
            </div>
            <slot name="raw"></slot>
            <div data-testId={`${this.dataTestId}-modal-footer`} class={footerClass}>
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    return (
      <Host
        class={{
          'modal-open': this.isOpen,
          visible: this.isOpen,
          invisible: !this.isOpen,
        }}>
        {this.renderOverlay()}
      </Host>
    );
  }
}
